import { onBeforeUnmount, ref, watch, type WatchSource } from 'vue'

/**
 * Keep a dialog mounted long enough to play its leave transition, then force
 * it out of the tree. Headless UI occasionally never finishes that transition;
 * while it is stuck, the dialog keeps `#app` inert and a full-viewport layer
 * keeps eating clicks.
 */
export function useMountedWhileOpen(open: WatchSource<boolean>, leaveMs = 450) {
  const mounted = ref(false)
  let timer: ReturnType<typeof setTimeout> | undefined

  watch(
    open,
    (isOpen) => {
      if (timer !== undefined) {
        clearTimeout(timer)
        timer = undefined
      }
      if (isOpen) {
        mounted.value = true
        return
      }
      if (!mounted.value) return
      timer = setTimeout(() => {
        timer = undefined
        mounted.value = false
      }, leaveMs)
    },
    { immediate: true },
  )

  onBeforeUnmount(() => {
    if (timer !== undefined) clearTimeout(timer)
  })

  return mounted
}
